import React from 'react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { LinkLayout } from '../../../../../../Shared/Frontend/utils/links'
import { Link } from '../../../utils/link'
import type { ActionProps } from '../../modernBlocks.state'
import { SharedBlockLayout } from '../shared/sharedBlock.layout'
import type { LinkListBlockClassNames, LinkListItem } from './linkListBlock.state'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { htmlDecode } from '../../../utils/html'

export const linkListBlockClasses: LinkListBlockClassNames = {
  // DEFAULTS
  block: 'block block--list block--linklist',
  inview: 'inview',
  inner: 'block__inner ls-container ls-gutter ls-spacing--s',
  header: 'block__header',
  label: 'block__label text--s',
  title: 'block__title title--m',
  intro: 'block__intro text--l',
  content: 'block__content',
  footer: 'block__footer ls-clickables',
  clickableWrapper: 'block__action ls-clickable',
  clickablePrimary: 'block__button button button--primary',
  clickableSecondary: 'block__button button button--tertiary',

  // BLOCK SPECIFIC
  list: 'list',
  item: 'list__item',
  itemClickable: 'list__link',
  itemText: 'list__inner',
  itemTitle: 'list__title',
  itemSubTitle: 'list__description text--s',
  itemIcon: 'list__icon',
}

export const LinkListBlockLayout = {
  Inner: (props: { children?: React.ReactNode; amountNested: number }): JSX.Element => {
    const { amountNested, children } = props
    return (
      <div className={getClassNameModern(linkListBlockClasses, 'inner', 'mw-container-s mx-auto px-gutter')}>
        {children}
      </div>
    )
  },
  Content: (
    props: {
      label?: string
      title?: string
      intro?: string
      items: LinkListItem[]
    } & ActionProps
  ) => {
    const { label, title, intro, items, showPrimary, showSecondary, primaryAction, secondaryAction } = props
    return (
      <>
        {(label || title || intro) && (
          <header className={getClassNameModern(linkListBlockClasses, 'header', '')}>
            {label && (
              <p className={getClassNameModern(linkListBlockClasses, 'label', 'fs-6 text-primary text-uppercase')}>
                {label}
              </p>
            )}
            {title && <h2 id={slugify(htmlDecode(title))} className={getClassNameModern(linkListBlockClasses, 'title', 'h2 mb-3')}>{title}</h2>}
            {intro && (
              <p
                className={getClassNameModern(linkListBlockClasses, 'intro', 'text--styled mb-3')}
                dangerouslySetInnerHTML={{ __html: intro }}
              />
            )}
          </header>
        )}


        <div className={getClassNameModern(linkListBlockClasses, 'content', '')}>
          {items && items.length > 0 && (
            <ul className={getClassNameModern(linkListBlockClasses, 'list', 'list-group mt-5')}>
              {props.items.map((item) => {
                const { key, link, subTitle } = item
                return (
                  link && (
                    <li
                      className={getClassNameModern(linkListBlockClasses, 'item', 'list-group-item')}
                      key={`linklist-${link}-${key}`}
                    >
                      <LinkLayout
                        link={link}
                        className={getClassNameModern(
                          linkListBlockClasses,
                          'itemClickable',
                          'd-flex align-items-baseline py-2 text-decoration-none'
                        )}
                      >
                        <LinkListBlockLayout.ItemInner itemLink={link} subTitle={subTitle} />
                      </LinkLayout>
                    </li>
                  )
                )
              })}
            </ul>
          )}

          {(showPrimary || showSecondary) && (
            <footer className={getClassNameModern(linkListBlockClasses, 'footer', 'pt-5')}>
              {showPrimary && primaryAction && primaryAction.title && primaryAction.url && (
                <div
                  className={getClassNameModern(linkListBlockClasses, 'clickableWrapper', 'd-block d-sm-inline-block')}
                >
                  <SharedBlockLayout.Clickable
                    type="button"
                    id={`linklistPrimaryAction-${primaryAction.url}`}
                    link={primaryAction}
                    classNames={getClassNameModern(linkListBlockClasses, 'clickablePrimary', 'btn btn-primary')}
                  />
                </div>
              )}
              {showSecondary && secondaryAction && secondaryAction.title && secondaryAction.url && (
                <div
                  className={getClassNameModern(
                    linkListBlockClasses,
                    'clickableWrapper',
                    'd-block d-sm-inline-block ms-sm-3 mt-3 mt-sm-0'
                  )}
                >
                  <SharedBlockLayout.Clickable
                    type="button"
                    id={`linklistSecondaryAction-${secondaryAction.url}`}
                    link={secondaryAction}
                    classNames={getClassNameModern(linkListBlockClasses, 'clickableSecondary', 'btn btn-outline-primary')}
                  />
                </div>
              )}
            </footer>
          )}
        </div>
      </>
    )
  },
  ItemInner: (props: { itemLink: Link; subTitle: string }): JSX.Element => {
    const { itemLink, subTitle } = props
    return (
      <>
        {itemLink.isLinkExternal ? (
          <SharedBlockLayout.SVGIcon iconId='#icon--external' width='24px' height='24px' className={getClassNameModern(linkListBlockClasses, 'itemIcon', 'me-3')} />
        ) : (
          <SharedBlockLayout.SVGIcon iconId='#icon--arrow-right' width='24px' height='24px'  className={getClassNameModern(linkListBlockClasses, 'itemIcon', 'me-3')} />
        )}

        <div className={getClassNameModern(linkListBlockClasses, 'itemText', '')}>
          {itemLink.title && (
            <p className={getClassNameModern(linkListBlockClasses, 'itemTitle', '')}>{itemLink.title}</p>
          )}

          {subTitle && <p className={getClassNameModern(linkListBlockClasses, 'itemSubTitle', ' small')}>{subTitle}</p>}
        </div>
      </>
    )
  }
}
